import { useEffect, useMemo, useState } from 'react';
/* eslint-disable react-hooks/exhaustive-deps -- loaders are intentionally keyed by tenant state */
import AdminLayout from '../../components/layout/AdminLayout';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

type SaasPlan = {
  id: string;
  codigo: string;
  nombre: string;
  moneda: string;
  precio: number;
  periodo_dias: number;
  activo: boolean;
};

type Gimnasio = {
  id: string;
  nombre: string;
  slug: string;
  estado: 'activo' | 'inactivo' | string;
  es_gimnasio_owner: boolean;
  saas_exento: boolean;
  progreso_sin_reserva: boolean;
  registro_mobile_visible: boolean;
  plan_saas: string | null;
  saas_plan_id: string | null;
  servicio_bloqueado: boolean;
  // PostgREST embed may come back as object or single-item array depending on schema relationship inference.
  saas_plan?:
    | {
        id: string;
        codigo: string;
        nombre: string;
      }
    | {
        id: string;
        codigo: string;
        nombre: string;
      }[]
    | null;
  created_at: string;
  updated_at: string;
};

type InviteResult = {
  message: string;
  access_link: string | null;
  temporary_password: string | null;
  created_auth_user: boolean;
};

type ServiceSnapshot = {
  gimnasio_id: string;
  estado: string | null;
  periodo_fin: string | null;
  operable: boolean;
  motivo: string;
  plan_nombre: string | null;
};

type GymSaasPayment = {
  id: string;
  monto: number;
  moneda: string;
  metodo: string;
  estado: string;
  referencia_externa: string | null;
  fecha_pago: string | null;
  created_at: string;
  notas: string | null;
  metadata: Record<string, unknown> | null;
};

const toSlug = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);

export default function GimnasiosPage() {
  const { profile } = useAuth();
  const [gimnasios, setGimnasios] = useState<Gimnasio[]>([]);
  const [snapshotsByGym, setSnapshotsByGym] = useState<Record<string, ServiceSnapshot>>({});
  const [saasPlanes, setSaasPlanes] = useState<SaasPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Gimnasio | null>(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteGym, setInviteGym] = useState<Gimnasio | null>(null);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteResult, setInviteResult] = useState<InviteResult | null>(null);
  const [toggleBlockSavingId, setToggleBlockSavingId] = useState<string | null>(null);
  const [showManualPayModal, setShowManualPayModal] = useState(false);
  const [manualPayGym, setManualPayGym] = useState<Gimnasio | null>(null);
  const [manualPaySaving, setManualPaySaving] = useState(false);
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [paymentsGym, setPaymentsGym] = useState<Gimnasio | null>(null);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);
  const [paymentsRows, setPaymentsRows] = useState<GymSaasPayment[]>([]);
  const [manualPayForm, setManualPayForm] = useState({
    amount: '',
    currency: 'CLP',
    method: 'transferencia',
    reference: '',
    notes: '',
    days: '',
  });
  const [inviteForm, setInviteForm] = useState({
    nombre: '',
    email: '',
  });
  const [form, setForm] = useState({
    nombre: '',
    slug: '',
    saas_plan_id: '',
    estado: 'activo' as 'activo' | 'inactivo',
    es_gimnasio_owner: false,
    saas_exento: false,
    progreso_sin_reserva: false,
    registro_mobile_visible: false,
  });

  const isOwner = profile?.role === 'platform_admin';

  const stats = useMemo(() => {
    const total = gimnasios.length;
    const activos = gimnasios.filter((g) => g.estado === 'activo').length;
    const inactivos = total - activos;
    return { total, activos, inactivos };
  }, [gimnasios]);

  const getPlanByCode = (code: string | null | undefined) => {
    if (!code) return null;
    return saasPlanes.find((p) => p.codigo.toLowerCase() === code.toLowerCase()) || null;
  };

  const SERVICE_REASON_LABEL: Record<string, string> = {
    operativo: 'Operativo',
    operativo_exento_owner: 'Operativo (owner exento)',
    gimnasio_inactivo: 'Gimnasio inactivo',
    bloqueo_manual_owner: 'Bloqueado por owner',
    sin_suscripcion: 'Sin suscripcion',
    suscripcion_suspendida: 'Suscripcion suspendida',
    suscripcion_vencida: 'Suscripcion vencida',
  };

  const fetchSaasPlanes = async () => {
    const { data, error } = await supabase
      .from('saas_planes')
      .select('id,codigo,nombre,moneda,precio,periodo_dias,activo')
      .order('precio', { ascending: true });
    if (error) throw error;
    const planes = ((data as SaasPlan[]) || []).filter((p) => p.activo);
    setSaasPlanes(planes);
    return planes;
  };

  const fetchGimnasios = async () => {
    try {
      setLoading(true);
      setError(null);
      const [gimRes, planRes] = await Promise.all([
        supabase
          .from('gimnasios')
          .select('id,nombre,slug,estado,es_gimnasio_owner,saas_exento,progreso_sin_reserva,registro_mobile_visible,plan_saas,saas_plan_id,servicio_bloqueado,created_at,updated_at,saas_plan:saas_plan_id(id,codigo,nombre)')
          .order('created_at', { ascending: false }),
        fetchSaasPlanes(),
      ]);

      if (gimRes.error) throw gimRes.error;
      const gyms = (gimRes.data as Gimnasio[]) || [];
      setGimnasios(gyms);

      const snapshots = await Promise.all(
        gyms.map(async (g) => {
          const { data: snapData, error: snapError } = await supabase.rpc('gym_service_snapshot', {
            target_gym_id: g.id,
          });
          if (snapError) throw snapError;
          const row = Array.isArray(snapData) && snapData.length > 0 ? (snapData[0] as ServiceSnapshot) : null;
          return row ? [g.id, row] : [g.id, null];
        })
      );

      const byGym = snapshots.reduce<Record<string, ServiceSnapshot>>((acc, tuple) => {
        const gymId = tuple[0] as string;
        const value = tuple[1] as ServiceSnapshot | null;
        if (value) acc[gymId] = value;
        return acc;
      }, {});
      setSnapshotsByGym(byGym);

      if (planRes.length > 0 && !form.saas_plan_id) {
        setForm((prev) => ({ ...prev, saas_plan_id: planRes[0].id }));
      }
    } catch (err: any) {
      setError(err?.message || 'No se pudo cargar gimnasios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOwner) fetchGimnasios();
    else setLoading(false);
  }, [isOwner]);

  const openCreate = () => {
    setEditing(null);
    setSlugTouched(false);
    setForm({
      nombre: '',
      slug: '',
      saas_plan_id: saasPlanes[0]?.id || '',
      estado: 'activo',
      es_gimnasio_owner: false,
      saas_exento: false,
      progreso_sin_reserva: false,
      registro_mobile_visible: false,
    });
    setShowModal(true);
  };

  const openEdit = (g: Gimnasio) => {
    const legacyPlan = getPlanByCode(g.plan_saas);
    setEditing(g);
    setSlugTouched(true);
    setForm({
      nombre: g.nombre,
      slug: g.slug,
      saas_plan_id: g.saas_plan_id || legacyPlan?.id || saasPlanes[0]?.id || '',
      estado: (g.estado === 'inactivo' ? 'inactivo' : 'activo'),
      es_gimnasio_owner: !!g.es_gimnasio_owner,
      saas_exento: !!g.saas_exento,
      progreso_sin_reserva: !!g.progreso_sin_reserva,
      registro_mobile_visible: !!g.registro_mobile_visible,
    });
    setShowModal(true);
  };

  const openInvite = (g: Gimnasio) => {
    setInviteGym(g);
    setInviteResult(null);
    setInviteForm({ nombre: '', email: '' });
    setShowInviteModal(true);
  };

  const openManualPay = (g: Gimnasio) => {
    const plan = saasPlanes.find((p) => p.id === g.saas_plan_id) || getPlanByCode(g.plan_saas || '') || null;
    setManualPayGym(g);
    setManualPayForm({
      amount: plan ? String(Number(plan.precio || 0)) : '',
      currency: plan?.moneda || 'CLP',
      method: 'transferencia',
      reference: '',
      notes: '',
      days: plan ? String(Number(plan.periodo_dias || 30)) : '30',
    });
    setShowManualPayModal(true);
  };

  const openPaymentsHistory = async (g: Gimnasio) => {
    try {
      setPaymentsGym(g);
      setPaymentsRows([]);
      setPaymentsError(null);
      setPaymentsLoading(true);
      setShowPaymentsModal(true);

      const { data, error } = await supabase
        .from('gimnasio_pagos')
        .select('id,monto,moneda,metodo,estado,referencia_externa,fecha_pago,created_at,notas,metadata')
        .eq('gimnasio_id', g.id)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setPaymentsRows((data as GymSaasPayment[]) || []);
    } catch (err: any) {
      setPaymentsError(err?.message || 'No se pudo cargar el historial de pagos SaaS');
    } finally {
      setPaymentsLoading(false);
    }
  };

  const inviteAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteGym) return;

    const email = inviteForm.email.trim().toLowerCase();
    const nombre = inviteForm.nombre.trim();

    if (!email || !email.includes('@')) {
      setError('Debes ingresar un email valido.');
      return;
    }

    try {
      setInviteSaving(true);
      setError(null);
      setInviteResult(null);

      const getValidAccessToken = async () => {
        let {
          data: { session },
        } = await supabase.auth.getSession();

        const now = Math.floor(Date.now() / 1000);
        const expiresAt = session?.expires_at ?? 0;
        const isExpiredOrNearExpiry = !session?.access_token || expiresAt <= now + 60;

        if (isExpiredOrNearExpiry) {
          const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
          if (refreshErr || !refreshed?.session?.access_token) {
            throw new Error('Sesion expirada. Vuelve a iniciar sesion.');
          }
          session = refreshed.session;
        }

        return session.access_token;
      };

      const invokeInvite = async (accessToken: string) =>
        supabase.functions.invoke('invitar-admin-gimnasio', {
          body: {
            gimnasio_id: inviteGym.id,
            email,
            nombre: nombre || null,
          },
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

      const parseInvokeError = (invokeError: any) => {
        const msg = String(invokeError?.message || '').toLowerCase();
        const status = invokeError?.context?.status;
        const body = invokeError?.context?.body || '';
        const bodyLc = String(body || '').toLowerCase();
        const invalidJwt =
          status === 401 ||
          msg.includes('invalid jwt') ||
          bodyLc.includes('invalid jwt') ||
          bodyLc.includes('auth session missing') ||
          bodyLc.includes('jwt');

        return { invalidJwt };
      };

      let accessToken = await getValidAccessToken();
      let { data: result, error: invokeError } = await invokeInvite(accessToken);

      if (invokeError && parseInvokeError(invokeError).invalidJwt) {
        const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
        if (refreshErr || !refreshed?.session?.access_token) {
          throw new Error('Sesion expirada. Vuelve a iniciar sesion.');
        }
        accessToken = refreshed.session.access_token;
        const retry = await invokeInvite(accessToken);
        result = retry.data;
        invokeError = retry.error;
      }

      if (invokeError) {
        let detailedError = invokeError.message || 'No se pudo invitar al administrador';
        const response = (invokeError as any)?.context;
        if (response && typeof response.json === 'function') {
          const body = await response.json().catch(() => null);
          if (body?.error) detailedError = body.error;
          else if (body?.message) detailedError = body.message;
        }
        throw new Error(detailedError);
      }

      setInviteResult({
        message: (result as any)?.message || 'Invitacion enviada.',
        access_link: (result as any)?.access_link || null,
        temporary_password: (result as any)?.temporary_password || null,
        created_auth_user: !!(result as any)?.created_auth_user,
      });
    } catch (err: any) {
      setError(err?.message || 'No se pudo invitar al administrador');
    } finally {
      setInviteSaving(false);
    }
  };

  const onNombreChange = (nombre: string) => {
    setForm((prev) => ({
      ...prev,
      nombre,
      slug: slugTouched ? prev.slug : toSlug(nombre),
    }));
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      setError('Nombre es obligatorio.');
      return;
    }
    if (!form.slug.trim()) {
      setError('Slug es obligatorio.');
      return;
    }
    if (!form.saas_plan_id) {
      setError('Debes seleccionar un plan SaaS.');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const selectedPlan = saasPlanes.find((p) => p.id === form.saas_plan_id);
      if (!selectedPlan) {
        throw new Error('Plan SaaS invalido.');
      }

      const payload = {
        nombre: form.nombre.trim(),
        slug: toSlug(form.slug),
        saas_plan_id: selectedPlan.id,
        plan_saas: selectedPlan.codigo,
        estado: form.estado,
        es_gimnasio_owner: form.es_gimnasio_owner,
        saas_exento: form.saas_exento || form.es_gimnasio_owner,
        progreso_sin_reserva: form.progreso_sin_reserva,
        registro_mobile_visible: form.es_gimnasio_owner ? false : form.registro_mobile_visible,
        updated_at: new Date().toISOString(),
      };

      if (editing) {
        if (form.es_gimnasio_owner) {
          const { error: clearOwnerError } = await supabase
            .from('gimnasios')
            .update({ es_gimnasio_owner: false, updated_at: new Date().toISOString() })
            .eq('es_gimnasio_owner', true)
            .neq('id', editing.id);
          if (clearOwnerError) throw clearOwnerError;
        }

        const { error } = await supabase.from('gimnasios').update(payload).eq('id', editing.id);
        if (error) throw error;

        if (editing.saas_plan_id !== selectedPlan.id) {
          const { error: assignError } = await supabase.rpc('assign_gym_saas_plan', {
            target_gym_id: editing.id,
            target_plan_id: selectedPlan.id,
          });
          if (assignError) throw assignError;
        }
      } else {
        if (form.es_gimnasio_owner) {
          const { error: clearOwnerError } = await supabase
            .from('gimnasios')
            .update({ es_gimnasio_owner: false, updated_at: new Date().toISOString() })
            .eq('es_gimnasio_owner', true);
          if (clearOwnerError) throw clearOwnerError;
        }

        const { data, error } = await supabase.from('gimnasios').insert([payload]).select('id').single();
        if (error) throw error;
        const gymId = (data as { id: string } | null)?.id;
        if (gymId) {
          const { error: assignError } = await supabase.rpc('assign_gym_saas_plan', {
            target_gym_id: gymId,
            target_plan_id: selectedPlan.id,
          });
          if (assignError) throw assignError;
        }
      }

      setShowModal(false);
      setEditing(null);
      await fetchGimnasios();
    } catch (err: any) {
      setError(err?.message || 'No se pudo guardar el gimnasio');
    } finally {
      setSaving(false);
    }
  };

  const toggleEstado = async (g: Gimnasio) => {
    try {
      setError(null);
      const next = g.estado === 'activo' ? 'inactivo' : 'activo';
      const { error } = await supabase
        .from('gimnasios')
        .update({ estado: next, updated_at: new Date().toISOString() })
        .eq('id', g.id);
      if (error) throw error;
      await fetchGimnasios();
    } catch (err: any) {
      setError(err?.message || 'No se pudo cambiar el estado');
    }
  };

  const toggleServiceBlock = async (g: Gimnasio) => {
    try {
      setError(null);
      setToggleBlockSavingId(g.id);
      const { error } = await supabase
        .from('gimnasios')
        .update({
          servicio_bloqueado: !g.servicio_bloqueado,
          updated_at: new Date().toISOString(),
        })
        .eq('id', g.id);
      if (error) throw error;
      await fetchGimnasios();
    } catch (err: any) {
      setError(err?.message || 'No se pudo actualizar el bloqueo manual');
    } finally {
      setToggleBlockSavingId(null);
    }
  };

  const deleteGym = async (g: Gimnasio) => {
    if (g.estado === 'activo') {
      setError('Solo puedes eliminar gimnasios inactivos.');
      return;
    }

    if (g.es_gimnasio_owner) {
      setError('No se puede eliminar el gimnasio owner principal.');
      return;
    }

    const confirmed = confirm(
      `¿Eliminar gimnasio "${g.nombre}"?\n\nEsta acción es permanente y puede fallar si el gimnasio tiene datos relacionados.`
    );
    if (!confirmed) return;

    try {
      setError(null);
      const { error } = await supabase.rpc('delete_inactive_gym', { p_gym_id: g.id });
      if (error) throw error;
      await fetchGimnasios();
    } catch (err: any) {
      setError(err?.message || 'No se pudo eliminar el gimnasio');
    }
  };

  const submitManualPay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualPayGym) return;

    try {
      setManualPaySaving(true);
      setError(null);

      const amount = manualPayForm.amount.trim() ? Number(manualPayForm.amount) : null;
      if (amount !== null && (!Number.isFinite(amount) || amount < 0)) {
        throw new Error('Monto manual invalido.');
      }

      const days = manualPayForm.days.trim() ? Number(manualPayForm.days) : null;
      if (days !== null && (!Number.isFinite(days) || days <= 0)) {
        throw new Error('Dias a agregar invalidos.');
      }

      const { error: rpcError } = await supabase.rpc('registrar_pago_saas_manual', {
        target_gym_id: manualPayGym.id,
        target_amount: amount,
        target_currency: manualPayForm.currency.trim() || null,
        target_method: manualPayForm.method.trim() || null,
        target_reference: manualPayForm.reference.trim() || null,
        target_notes: manualPayForm.notes.trim() || null,
        target_days: days,
      });

      if (rpcError) throw rpcError;

      setShowManualPayModal(false);
      setManualPayGym(null);
      await fetchGimnasios();
    } catch (err: any) {
      setError(err?.message || 'No se pudo registrar el pago manual');
    } finally {
      setManualPaySaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gimnasios</h1>
            <p className="text-gray-600 mt-1">Gestión SaaS de gimnasios (solo owner)</p>
          </div>
          {isOwner && (
            <button
              onClick={openCreate}
              className="bg-teal-500 text-white px-6 py-3 rounded-lg hover:bg-teal-600 transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-lg"></i>
              Nuevo Gimnasio
            </button>
          )}
        </div>

        {!isOwner ? (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg">
            Solo el perfil owner (`platform_admin`) puede crear y administrar gimnasios.
          </div>
        ) : null}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {error}
          </div>
        )}

        {isOwner && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <p className="text-gray-600 text-sm">Total</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <p className="text-gray-600 text-sm">Activos</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{stats.activos}</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <p className="text-gray-600 text-sm">Inactivos</p>
                <p className="text-3xl font-bold text-gray-600 mt-1">{stats.inactivos}</p>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-20 text-gray-500">Cargando gimnasios...</div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {gimnasios.map((g) => {
                  const isOwnerGym = !!g.es_gimnasio_owner;
                  const snapshot = snapshotsByGym[g.id];
                  const isOwnerExempt = snapshot?.motivo === 'operativo_exento_owner';
                  const isSaasExempt = !!g.saas_exento || isOwnerGym || isOwnerExempt;
                  const statusLabel = snapshot
                    ? (SERVICE_REASON_LABEL[snapshot.motivo] || snapshot.motivo || 'Sin datos')
                    : 'Sin datos';
                  const embeddedPlan = Array.isArray(g.saas_plan) ? g.saas_plan[0] : g.saas_plan;

                  return (
                    <div key={g.id} className="bg-white border border-gray-200 rounded-xl p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">{g.nombre}</h3>
                          <p className="text-sm text-gray-500 mt-1">Slug: {g.slug}</p>
                      {isOwnerGym ? (
                        <p className="text-xs text-teal-700 mt-1 font-medium">Gimnasio owner principal</p>
                      ) : null}
                      {isSaasExempt && !isOwnerGym ? (
                        <p className="text-xs text-teal-700 mt-1 font-medium">Exento de pago SaaS</p>
                      ) : null}
                      {g.progreso_sin_reserva ? (
                        <p className="text-xs text-indigo-700 mt-1 font-medium">Progreso sin reserva: habilitado</p>
                      ) : null}
                      <p className={`text-xs mt-1 font-medium ${g.registro_mobile_visible ? 'text-blue-700' : 'text-gray-500'}`}>
                        Registro móvil: {g.registro_mobile_visible ? 'visible' : 'oculto'}
                      </p>
                      <p className="text-sm text-gray-500">
                        Plan:{' '}
                        {embeddedPlan?.nombre ||
                          getPlanByCode(g.plan_saas)?.nombre ||
                              g.plan_saas ||
                              'sin plan'}
                          </p>
                          <p className="text-sm text-gray-500">
                            Servicio:{' '}
                            {snapshot?.operable
                              ? isSaasExempt
                                ? 'operativo (exento de pago)'
                                : 'operativo'
                              : statusLabel.toLowerCase()}
                          </p>
                          {isSaasExempt ? (
                            <p className="text-xs text-gray-400 mt-1">Sin vencimiento (gimnasio exento de pago)</p>
                          ) : snapshot?.periodo_fin ? (
                            <p className="text-xs text-gray-400 mt-1">
                              Vigente hasta: {new Date(snapshot.periodo_fin).toLocaleDateString('es-CL')}
                            </p>
                          ) : null}
                        </div>
                        <div className="flex flex-col gap-2 items-end">
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                              g.estado === 'activo'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {g.estado}
                          </span>
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                              g.servicio_bloqueado
                                ? 'bg-red-100 text-red-700'
                                : 'bg-teal-100 text-teal-700'
                            }`}
                          >
                            {g.servicio_bloqueado ? 'bloqueado' : 'habilitado'}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mt-5 pt-4 border-t border-gray-200">
                        <button
                          onClick={() => openEdit(g)}
                          className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 cursor-pointer"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => openInvite(g)}
                          className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 cursor-pointer"
                        >
                          Invitar admin
                        </button>
                        {g.estado === 'inactivo' && !g.es_gimnasio_owner ? (
                          <button
                            onClick={() => deleteGym(g)}
                            className="px-4 py-2 text-sm rounded-lg border border-red-300 text-red-700 hover:bg-red-50 cursor-pointer"
                          >
                            Eliminar
                          </button>
                        ) : null}

                        {isSaasExempt ? (
                          <span className="text-xs text-gray-500 ml-2">
                            Acciones de cobro/bloqueo no aplican para gimnasio exento de pago.
                          </span>
                        ) : (
                          <>
                            <button
                              onClick={() => openManualPay(g)}
                              className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 cursor-pointer"
                            >
                              Registrar pago
                            </button>
                            <button
                              onClick={() => openPaymentsHistory(g)}
                              className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 cursor-pointer"
                            >
                              Ver pagos SaaS
                            </button>
                            <button
                              onClick={() => toggleServiceBlock(g)}
                              disabled={toggleBlockSavingId === g.id}
                              className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-60 cursor-pointer"
                            >
                              {toggleBlockSavingId === g.id
                                ? 'Actualizando...'
                                : g.servicio_bloqueado
                                ? 'Desbloquear servicio'
                                : 'Bloquear servicio'}
                            </button>
                            <button
                              onClick={() => toggleEstado(g)}
                              className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 cursor-pointer"
                            >
                              {g.estado === 'activo' ? 'Desactivar' : 'Activar'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-xl w-full">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">{editing ? 'Editar Gimnasio' : 'Nuevo Gimnasio'}</h2>
              </div>

              <form onSubmit={save} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
                  <input
                    required
                    value={form.nombre}
                    onChange={(e) => onNombreChange(e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Ej: Fit Center Las Condes"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Slug *</label>
                  <input
                    required
                    value={form.slug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      setForm((prev) => ({ ...prev, slug: toSlug(e.target.value) }));
                    }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="fit-center-las-condes"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Plan SaaS</label>
                    <select
                      value={form.saas_plan_id}
                      onChange={(e) => setForm((prev) => ({ ...prev, saas_plan_id: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      {saasPlanes.length === 0 ? (
                        <option value="">Sin planes disponibles</option>
                      ) : (
                        saasPlanes.map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            {plan.nombre} ({plan.codigo})
                          </option>
                        ))
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                    <select
                      value={form.estado}
                      onChange={(e) => setForm((prev) => ({ ...prev, estado: e.target.value as 'activo' | 'inactivo' }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                    </select>
                  </div>
                </div>

                <div className="rounded-lg border border-teal-200 bg-teal-50 p-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.es_gimnasio_owner}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          es_gimnasio_owner: e.target.checked,
                          saas_exento: e.target.checked ? true : prev.saas_exento,
                        }))
                      }
                      className="h-4 w-4 text-teal-600 border-gray-300 rounded"
                    />
                    <div>
                      <p className="text-sm font-medium text-teal-800">Gimnasio owner principal</p>
                      <p className="text-xs text-teal-700">
                        Exento de cobro SaaS. No aplica registrar pago, bloquear servicio ni desactivar.
                      </p>
                      <p className="text-xs text-teal-700 mt-1">
                        Solo puede existir 1 gimnasio owner. Al activarlo, se desmarca automáticamente en los demás.
                      </p>
                    </div>
                  </label>
                </div>

                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.saas_exento}
                      disabled={form.es_gimnasio_owner}
                      onChange={(e) => setForm((prev) => ({ ...prev, saas_exento: e.target.checked }))}
                      className="h-4 w-4 text-emerald-600 border-gray-300 rounded disabled:opacity-60"
                    />
                    <div>
                      <p className="text-sm font-medium text-emerald-800">Exento de pago SaaS</p>
                      <p className="text-xs text-emerald-700">
                        Al estar activo, este gimnasio no requiere pagos SaaS periodicos y queda operativo sin vencimiento.
                      </p>
                      {form.es_gimnasio_owner ? (
                        <p className="text-xs text-emerald-700 mt-1">Se marca automaticamente por ser gimnasio owner principal.</p>
                      ) : null}
                    </div>
                  </label>
                </div>

                <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.progreso_sin_reserva}
                      onChange={(e) => setForm((prev) => ({ ...prev, progreso_sin_reserva: e.target.checked }))}
                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                    <div>
                      <p className="text-sm font-medium text-indigo-800">Permitir progreso sin reserva</p>
                      <p className="text-xs text-indigo-700">
                        Si está activo, los usuarios podrán marcar ejercicios como completados en cualquier momento
                        (no se exige reserva ni bloque horario).
                      </p>
                    </div>
                  </label>
                </div>

                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <label className={`flex items-center gap-3 ${form.es_gimnasio_owner ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={!form.es_gimnasio_owner && form.registro_mobile_visible}
                      disabled={form.es_gimnasio_owner}
                      onChange={(e) => setForm((prev) => ({ ...prev, registro_mobile_visible: e.target.checked }))}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded disabled:opacity-60"
                    />
                    <div>
                      <p className="text-sm font-medium text-blue-800">Visible en registro móvil</p>
                      <p className="text-xs text-blue-700">
                        Permite que una persona seleccione este gimnasio al crear una cuenta desde la app.
                      </p>
                      {form.es_gimnasio_owner ? (
                        <p className="text-xs text-blue-700 mt-1">El gimnasio owner de plataforma nunca puede publicarse.</p>
                      ) : null}
                    </div>
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditing(null);
                    }}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 px-6 py-3 bg-teal-500 disabled:opacity-70 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {saving ? 'Guardando...' : editing ? 'Guardar cambios' : 'Crear gimnasio'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showInviteModal && inviteGym && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-xl w-full">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">Invitar admin del gimnasio</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Gimnasio: <span className="font-medium text-gray-700">{inviteGym.nombre}</span>
                </p>
              </div>

              <form onSubmit={inviteAdmin} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
                  <input
                    value={inviteForm.nombre}
                    onChange={(e) => setInviteForm((prev) => ({ ...prev, nombre: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Nombre del administrador"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={inviteForm.email}
                    onChange={(e) => setInviteForm((prev) => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="admin@cliente.com"
                  />
                </div>

                {inviteResult && (
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 space-y-2">
                    <p className="text-sm font-medium text-teal-800">{inviteResult.message}</p>
                    {inviteResult.access_link && (
                      <div>
                        <p className="text-xs text-teal-700 mb-1">Link para definir contrasena</p>
                        <div className="text-xs text-teal-900 bg-white border border-teal-200 rounded p-2 break-all">
                          {inviteResult.access_link}
                        </div>
                      </div>
                    )}
                    {inviteResult.temporary_password && (
                      <div>
                        <p className="text-xs text-teal-700 mb-1">Contrasena temporal</p>
                        <div className="text-xs text-teal-900 bg-white border border-teal-200 rounded p-2 break-all">
                          {inviteResult.temporary_password}
                        </div>
                      </div>
                    )}
                    {!inviteResult.access_link && !inviteResult.temporary_password && (
                      <p className="text-xs text-teal-700">
                        El usuario ya existia. Se actualizo como gym_admin del gimnasio.
                      </p>
                    )}
                  </div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowInviteModal(false);
                      setInviteGym(null);
                      setInviteResult(null);
                    }}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cerrar
                  </button>
                  <button
                    type="submit"
                    disabled={inviteSaving}
                    className="flex-1 px-6 py-3 bg-teal-500 disabled:opacity-70 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {inviteSaving ? 'Invitando...' : 'Invitar administrador'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showManualPayModal && manualPayGym && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-xl w-full">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">Registrar pago SaaS manual</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Gimnasio: <span className="font-medium text-gray-700">{manualPayGym.nombre}</span>
                </p>
              </div>

              <form onSubmit={submitManualPay} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Monto</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={manualPayForm.amount}
                      onChange={(e) => setManualPayForm((prev) => ({ ...prev, amount: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="89900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Moneda</label>
                    <input
                      value={manualPayForm.currency}
                      onChange={(e) => setManualPayForm((prev) => ({ ...prev, currency: e.target.value.toUpperCase() }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="CLP"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Metodo</label>
                    <select
                      value={manualPayForm.method}
                      onChange={(e) => setManualPayForm((prev) => ({ ...prev, method: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="transferencia">Transferencia</option>
                      <option value="efectivo">Efectivo</option>
                      <option value="tarjeta">Tarjeta</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Dias a agregar</label>
                    <input
                      type="number"
                      min="1"
                      value={manualPayForm.days}
                      onChange={(e) => setManualPayForm((prev) => ({ ...prev, days: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      placeholder="30"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Referencia externa</label>
                  <input
                    value={manualPayForm.reference}
                    onChange={(e) => setManualPayForm((prev) => ({ ...prev, reference: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="TRX-123456"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notas</label>
                  <textarea
                    value={manualPayForm.notes}
                    onChange={(e) => setManualPayForm((prev) => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Pago registrado por transferencia bancaria."
                  />
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                  Esta accion extiende/activa la suscripcion del gimnasio y desbloquea el servicio si estaba bloqueado.
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowManualPayModal(false);
                      setManualPayGym(null);
                    }}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={manualPaySaving}
                    className="flex-1 px-6 py-3 bg-teal-500 disabled:opacity-70 text-white rounded-lg hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {manualPaySaving ? 'Registrando...' : 'Registrar pago'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showPaymentsModal && paymentsGym && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full">
              <div className="p-6 border-b border-gray-200 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Pagos SaaS del gimnasio</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Gimnasio: <span className="font-medium text-gray-700">{paymentsGym.nombre}</span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowPaymentsModal(false);
                    setPaymentsGym(null);
                    setPaymentsRows([]);
                    setPaymentsError(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="p-6">
                {paymentsLoading ? (
                  <div className="text-sm text-gray-500">Cargando historial...</div>
                ) : paymentsError ? (
                  <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                    {paymentsError}
                  </div>
                ) : paymentsRows.length === 0 ? (
                  <div className="text-sm text-gray-500">Aun no hay pagos SaaS para este gimnasio.</div>
                ) : (
                  <div className="max-h-[60vh] overflow-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr className="text-left text-gray-600">
                          <th className="px-4 py-3 font-medium">Fecha</th>
                          <th className="px-4 py-3 font-medium">Monto</th>
                          <th className="px-4 py-3 font-medium">Metodo</th>
                          <th className="px-4 py-3 font-medium">Estado</th>
                          <th className="px-4 py-3 font-medium">Referencia</th>
                          <th className="px-4 py-3 font-medium">Proveedor</th>
                          <th className="px-4 py-3 font-medium">Notas</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {paymentsRows.map((row) => {
                          const metadata =
                            row.metadata && typeof row.metadata === 'object' ? row.metadata : {};
                          const provider = String(metadata['proveedor'] || metadata['provider'] || '-');
                          const statusCls =
                            row.estado === 'paid'
                              ? 'bg-green-100 text-green-700'
                              : row.estado === 'pending'
                              ? 'bg-amber-100 text-amber-700'
                              : row.estado === 'failed'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-700';

                          return (
                            <tr key={row.id}>
                              <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                                {new Date(row.fecha_pago || row.created_at).toLocaleString('es-CL')}
                              </td>
                              <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                                {row.moneda} ${Number(row.monto || 0).toLocaleString('es-CL')}
                              </td>
                              <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{row.metodo || '-'}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusCls}`}>
                                  {row.estado}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                                {row.referencia_externa || '-'}
                              </td>
                              <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{provider}</td>
                              <td className="px-4 py-3 text-gray-700">
                                <div className="max-w-[18rem] truncate" title={row.notas || ''}>
                                  {row.notas || '-'}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
